import React, { useState, useEffect, useRef } from "react";
import { Outlet, Navigate, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Truck, MessageCircle, Settings, LogOut, Menu, X, ChevronRight, Map, MapPin, ShieldAlert, RefreshCw } from "lucide-react";
import Logo from "@/components/layout/Logo";
import DriverPrecheck from "@/pages/DriverPrecheck";
import { api } from "@/lib/api";

// Local device date (YYYY-MM-DD) — keys roll over at the driver's local midnight, not UTC.
function localDateStr() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function todayKey(userId) {
  return `driver_precheck_${userId}_${localDateStr()}`;
}

function logIdKey(userId) {
  return `driver_log_id_${userId}_${localDateStr()}`;
}

const NAV_ITEMS = [
  { to: "/driver-dashboard/runs",     label: "My Runs",  icon: Truck },
  { to: "/driver-dashboard/route",    label: "My Route", icon: Map },
  { to: "/driver-dashboard/messages", label: "Messages", icon: MessageCircle },
  { to: "/driver-dashboard/settings", label: "Settings", icon: Settings },
];

export default function DriverDashboardLayout() {
  const { user, loading, logout } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [precheckDone, setPrecheckDone] = useState(false);
  const [gpsActive, setGpsActive] = useState(false);   // a fix was received & sent recently
  // Location sharing is mandatory for a driver — not a preference. 'checking' until
  // the first fix or error arrives; 'granted' unlocks the app; 'blocked' shows a
  // full-screen gate the driver cannot dismiss or work around.
  const [locationStatus, setLocationStatus] = useState("checking");
  const gpsWatchRef = useRef(null);
  const lastSentRef = useRef(0);
  const [retryTick, setRetryTick] = useState(0); // bump to force the watch effect to re-run

  // Precheck is gated on a date-stamped key, so it persists across logins within
  // the same day and resets automatically at the driver's local midnight. Poll once
  // a minute so an app left open overnight re-shows the checklist after midnight.
  useEffect(() => {
    if (!user?.id) return;
    const check = () => setPrecheckDone(!!localStorage.getItem(todayKey(user.id)));
    check();
    const timer = setInterval(check, 60000);
    return () => clearInterval(timer);
  }, [user?.id]);

  // Opportunistically detect an existing denial via the Permissions API (not
  // supported everywhere, e.g. iOS Safari) so the blocker can show immediately
  // instead of waiting for watchPosition's first error.
  useEffect(() => {
    if (!precheckDone || !("permissions" in navigator)) return;
    let cancelled = false;
    navigator.permissions.query({ name: "geolocation" }).then((status) => {
      if (cancelled) return;
      if (status.state === "denied") setLocationStatus("blocked");
      status.onchange = () => {
        if (status.state === "denied") setLocationStatus("blocked");
      };
    }).catch(() => { /* Permissions API not supported for geolocation */ });
    return () => { cancelled = true; };
  }, [precheckDone, retryTick]);

  // ── GPS tracking: mandatory once precheck is done. Starting the watch is what
  // triggers the OS location-permission prompt after login; a denial blocks the
  // rest of the app rather than just flagging a status pill.
  useEffect(() => {
    if (!precheckDone || !user?.id || !("geolocation" in navigator)) {
      setGpsActive(false);
      return;
    }

    const sendLocation = async (position) => {
      setLocationStatus("granted");
      const now = Date.now();
      if (now - lastSentRef.current < 20000) return; // throttle to 1 update / 20s
      lastSentRef.current = now;
      try {
        await api.post("/drivers/location", {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          heading: position.coords.heading,
          speed: position.coords.speed,
        });
        setGpsActive(true);
      } catch {
        // Non-critical — silently ignore if offline
      }
    };

    gpsWatchRef.current = navigator.geolocation.watchPosition(
      sendLocation,
      (err) => {
        setGpsActive(false);
        if (err && err.code === 1) setLocationStatus("blocked"); // PERMISSION_DENIED
      },
      { enableHighAccuracy: true, maximumAge: 15000, timeout: 30000 }
    );

    return () => {
      if (gpsWatchRef.current !== null) {
        navigator.geolocation.clearWatch(gpsWatchRef.current);
        gpsWatchRef.current = null;
      }
    };
  }, [precheckDone, user?.id, retryTick]);

  const retryLocation = () => {
    setLocationStatus("checking");
    setRetryTick((t) => t + 1);
  };

  const handlePrecheckComplete = async ({ answers, comment, items }) => {
    const completedAt = new Date().toISOString();
    localStorage.setItem(todayKey(user.id), JSON.stringify({ answers, comment, completedAt }));
    const allPassed = Object.values(answers).every((v) => v === "Yes" || v === "N/A");
    try {
      const { data } = await api.post("/driver-logs", {
        precheck_all_passed: allPassed,
        precheck_comment: comment || "",
        precheck_items: items || [],
        login_at: completedAt,
      });
      if (data?.id) localStorage.setItem(logIdKey(user.id), data.id);
    } catch {
      // Non-critical — log locally even if server call fails
    }
    setPrecheckDone(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f] text-white/60">
        Loading…
      </div>
    );
  }

  if (!user) return <Navigate to="/driver-login" replace />;
  if (user.role !== "driver") return <Navigate to="/dashboard" replace />;

  const onLogout = async () => {
    if (user?.id) {
      const logId = localStorage.getItem(logIdKey(user.id));
      if (logId) {
        try { await api.patch(`/driver-logs/${logId}/logout`); } catch { /* non-critical */ }
        localStorage.removeItem(logIdKey(user.id));
      }
      // NOTE: precheck key is intentionally kept so the driver isn't asked to
      // re-confirm the checklist on every login — it auto-resets at local midnight.
    }
    await logout();
    navigate("/driver-login");
  };

  // GPS pill appearance reflects: blocked → acquiring → live. No off state —
  // location sharing is mandatory while driving, not a driver preference.
  const gpsLabel = locationStatus === "blocked" ? "GPS Blocked" : gpsActive ? "GPS On" : "Locating…";
  const gpsStyle = locationStatus === "blocked"
    ? { background: "rgba(239,68,68,0.12)", color: "#ef4444" }
    : gpsActive
      ? { background: "rgba(34,197,94,0.12)", color: "#22c55e" }
      : { background: "rgba(245,158,11,0.12)", color: "#f59e0b" };

  if (!precheckDone) {
    return <DriverPrecheck driverName={user.name} onComplete={handlePrecheckComplete} />;
  }

  // Location is mandatory once precheck is done — block the entire app (including
  // every nested route rendered via <Outlet/> below) until it's granted. A driver
  // cannot dismiss this or otherwise keep working with location off/denied.
  if (locationStatus !== "granted") {
    const blocked = locationStatus === "blocked";
    return (
      <div className="driver-bg min-h-screen text-white flex flex-col items-center justify-center px-6 text-center">
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center mb-5"
          style={{ background: blocked ? "rgba(239,68,68,0.12)" : "rgba(245,158,11,0.12)", color: blocked ? "#ef4444" : "#f59e0b" }}
        >
          <ShieldAlert size={30} />
        </div>
        <h2 className="brand-title text-lg mb-2">
          {blocked ? "Location Access Blocked" : "Location Access Required"}
        </h2>
        <p className="text-white/60 text-sm max-w-sm mb-1">
          {blocked
            ? "The driver app needs your location to track runs and can't be used until it's allowed."
            : "Waiting for location permission — please allow location access when prompted."}
        </p>
        {blocked && (
          <p className="text-white/40 text-xs max-w-sm mb-6">
            Enable location for this app in your browser or device settings, then try again.
          </p>
        )}
        <button
          onClick={retryLocation}
          className="mt-4 flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold"
          style={{ background: "rgba(245,158,11,0.15)", color: "#f59e0b" }}
          data-testid="location-retry-btn"
        >
          <RefreshCw size={15} /> Try Again
        </button>
        <button
          onClick={onLogout}
          className="mt-3 text-white/40 hover:text-white/70 text-xs font-semibold"
          data-testid="driver-logout-blocked"
        >
          Logout
        </button>
      </div>
    );
  }

  return (
    <div className="driver-bg min-h-screen text-white flex flex-col">
      {/* ── Top Bar ── */}
      <header
        className="driver-header sticky top-0 z-40 px-4 py-3 flex items-center justify-between gap-4"
        style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top, 0px))" }}
      >
        {/* Hamburger (mobile only) */}
        <button
          className="lg:hidden p-2 rounded-lg text-white/60 hover:text-amber-400 hover:bg-amber-400/10 transition-colors"
          onClick={() => setSidebarOpen(true)}
          aria-label="Open menu"
        >
          <Menu size={22} />
        </button>

        <div className="flex items-center gap-2">
          <Logo size={34} />
          <span
            className="brand-title text-xs hidden sm:block"
            style={{ color: "#f59e0b", letterSpacing: "0.14em" }}
          >
            DRIVER
          </span>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 ml-auto">
          <span className="hidden lg:block text-white/50 text-sm">Welcome, {user.name}</span>

          {/* GPS status — read-only indicator. Location sharing is mandatory for
              drivers, so there is no way to toggle it off from within the app. */}
          <span
            title="Location sharing is required and cannot be turned off"
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold select-none"
            style={gpsStyle}
            data-testid="gps-status"
          >
            <MapPin size={13} />
            <span>{gpsLabel}</span>
          </span>

          <button
            onClick={onLogout}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-white/60 hover:text-white hover:bg-white/10 transition-colors"
            data-testid="driver-logout"
          >
            <LogOut size={16} />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </header>

      <div className="flex flex-1">
        {/* ── Desktop Sidebar ── */}
        <aside className="driver-sidebar w-[240px] hidden lg:flex flex-col py-6 px-3">
          <div className="px-3 mb-6">
            <Logo size={44} />
            <div className="mt-4">
              <p className="brand-title text-sm" style={{ color: "#f59e0b" }}>
                {user.name?.toUpperCase()}
              </p>
              <p className="text-white/40 text-xs mt-0.5 uppercase tracking-widest">Driver</p>
            </div>
          </div>

          <nav className="flex flex-col gap-1.5 flex-1">
            {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  `driver-sidebar-item ${isActive ? "active" : ""}`
                }
              >
                <span
                  className="w-7 h-7 rounded-full flex items-center justify-center"
                  style={{ background: "rgba(245,158,11,0.15)", color: "#f59e0b" }}
                >
                  <Icon size={15} />
                </span>
                {label}
              </NavLink>
            ))}
          </nav>

          <button
            onClick={onLogout}
            className="driver-sidebar-item mt-4 text-white/50 hover:text-white/80"
          >
            <span
              className="w-7 h-7 rounded-full flex items-center justify-center"
              style={{ background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.5)" }}
            >
              <LogOut size={15} />
            </span>
            Logout
          </button>
        </aside>

        {/* ── Mobile Sidebar Drawer ── */}
        {sidebarOpen && (
          <>
            <div
              className="sidebar-overlay lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
            <aside className="sidebar-drawer driver-sidebar open lg:hidden px-3 flex flex-col"
              style={{ paddingTop: "calc(1.5rem + env(safe-area-inset-top, 0px))", paddingBottom: "1.5rem" }}>
              <div className="flex items-center justify-between px-3 mb-6">
                <Logo size={38} />
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="p-1.5 rounded-lg text-white/50 hover:text-white"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="px-3 mb-4">
                <p className="brand-title text-sm" style={{ color: "#f59e0b" }}>
                  {user.name?.toUpperCase()}
                </p>
                <p className="text-white/40 text-xs mt-0.5 uppercase tracking-widest">Driver</p>
              </div>

              <nav className="flex flex-col gap-1.5 flex-1">
                {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
                  <NavLink
                    key={to}
                    to={to}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      `driver-sidebar-item ${isActive ? "active" : ""}`
                    }
                  >
                    <span
                      className="w-7 h-7 rounded-full flex items-center justify-center"
                      style={{ background: "rgba(245,158,11,0.15)", color: "#f59e0b" }}
                    >
                      <Icon size={15} />
                    </span>
                    {label}
                    <ChevronRight size={14} className="ml-auto text-white/30" />
                  </NavLink>
                ))}
              </nav>

              <button
                onClick={onLogout}
                className="driver-sidebar-item mt-4 text-white/50"
              >
                <span
                  className="w-7 h-7 rounded-full flex items-center justify-center"
                  style={{ background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.4)" }}
                >
                  <LogOut size={15} />
                </span>
                Logout
              </button>
            </aside>
          </>
        )}

        {/* ── Main Content ── */}
        <main
          className={`flex-1 app-content ${theme === "light" ? "theme-light" : ""}`}
          data-testid="driver-dashboard-main"
        >
          <div className="p-4 sm:p-6 md:p-8 fade-up driver-content-pad lg:pb-8">
            <Outlet />
          </div>
        </main>
      </div>

      {/* ── Mobile Bottom Navigation ── */}
      <nav className="mobile-bottom-nav lg:hidden" data-testid="driver-mobile-nav">
        {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `mobile-bottom-nav-item ${isActive ? "active" : ""}`
            }
          >
            <Icon size={22} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
